// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/ban-ts-comment, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return */

// @ts-expect-error
const aerialApiUri: string = process.env.aerialPlotConnections.aerialPlotApi.baseUrl;
// @ts-expect-error
const apDataPlatformBaseUrl: string = process.env.aerialPlotConnections.aerialDataPlatformApi.baseUrl;

import { queryOptions } from "@tanstack/react-query";
import { AerialPlotResponse, Message } from "./types";
import type { FeatureCollection, Polygon, MultiPolygon } from "geojson";
import {
	ExperimentalDesignFactor,
	ExperimentalDesignType,
	ExperimentalDesignLayoutValidation,
	ExperimentalDesignForMFA,
	ExperimentalModalInfo,
	FactorPlot,
} from "./types/factorTypes";

export async function getExperimentalDesigns(
	fieldId: string,
	clientId: string,
	accessToken: string,
): Promise<AerialPlotResponse<ExperimentalDesignFactor[]>> {
	const res = await fetch(
		`${aerialApiUri}api/v1/Factors/GetExperimentalDesignsForField?fieldId=${fieldId}&clientId=${clientId}`,
		{
			method: "GET",
			headers: {
				Authorization: `Bearer ${accessToken}`,
			},
		},
	);

	const data = res.json();
	return data as Promise<AerialPlotResponse<ExperimentalDesignFactor[]>>;
}

export async function getExperimentalDesignTypes(
	accessToken: string,
): Promise<AerialPlotResponse<ExperimentalDesignType[]>> {
	const res = await fetch(`${aerialApiUri}api/v1/Factors/GetExperimentalDesignTypes`, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	});

	const data = res.json();
	return data as Promise<AerialPlotResponse<ExperimentalDesignType[]>>;
}

export async function inspectExperimentalDesignFile(
	file: File,
	clientId: string,
	fieldId: string,
	experimentalDesignId: string | null,
	accessToken: string,
): Promise<AerialPlotResponse<ExperimentalDesignLayoutValidation>> {
	const formData = new FormData();

	formData.append("file", file, file.name);

	const res = await fetch(
		`${aerialApiUri}api/v1/Factors/InspectExperimentalDesignFile?clientId=${clientId}&fieldId=${fieldId}${experimentalDesignId ? `&experimentalDesignId=${experimentalDesignId}` : ""}`,
		{
			method: "POST",
			headers: {
				Authorization: `Bearer ${accessToken}`,
			},
			body: formData,
		},
	);

	const data = res.json();
	return data as Promise<AerialPlotResponse<ExperimentalDesignLayoutValidation>>;
}

export async function createExperimentalDesign(
	experimentalDesignInfo: ExperimentalModalInfo,
	fieldId: string,
	clientId: string,
	accessToken: string,
): Promise<AerialPlotResponse<ExperimentalDesignFactor>> {
	const expDesignForm = new FormData();

	expDesignForm.set("FieldId", fieldId);
	expDesignForm.set("FactorTypeId", experimentalDesignInfo.typeId ?? "");
	expDesignForm.set("Name", experimentalDesignInfo.factorName ?? "");
	expDesignForm.set("Default", experimentalDesignInfo.default.toString());

	experimentalDesignInfo.plots?.forEach((plot: FactorPlot, i: number) => {
		expDesignForm.set(`FactorPlots[${i}].PlotId`, plot.plotId);
		expDesignForm.set(`FactorPlots[${i}].Replicate`, plot.replicate?.toString() ?? "");
		expDesignForm.set(`FactorPlots[${i}].PlotGroupingDisplayId`, plot.plotGroupingDisplayId ?? "");
		expDesignForm.set(`FactorPlots[${i}].TreatmentDisplayId`, plot.treatmentDisplayId?.toString() ?? "");
		expDesignForm.set(`FactorPlots[${i}].TreatmentName`, plot.treatmentName ?? "");
		expDesignForm.set(`FactorPlots[${i}].Seed`, plot.seed ?? "");
		expDesignForm.set(`FactorPlots[${i}].RecId`, plot.recId ?? "");
	});

	const res = await fetch(`${aerialApiUri}api/v1/Factors/CreateExperimentalDesign?clientId=${clientId}`, {
		method: "POST",
		headers: { Authorization: `Bearer ${accessToken}` },
		body: expDesignForm,
	});

	const data = res.json();
	return data as Promise<AerialPlotResponse<ExperimentalDesignFactor>>;
}

export async function editExperimentalDesign(
	experimentalDesignInfo: ExperimentalModalInfo,
	fieldId: string,
	clientId: string,
	accessToken: string,
): Promise<AerialPlotResponse<ExperimentalDesignFactor>> {
	const expDesignForm = new FormData();

	expDesignForm.set("Id", experimentalDesignInfo.designId ?? "");
	expDesignForm.set("FieldId", fieldId);
	expDesignForm.set("FactorTypeId", experimentalDesignInfo.typeId ?? "");
	expDesignForm.set("Name", experimentalDesignInfo.factorName ?? "");

	experimentalDesignInfo.plots?.forEach((plot: FactorPlot, i: number) => {
		expDesignForm.set(`FactorPlots[${i}].PlotId`, plot.plotId);
		expDesignForm.set(`FactorPlots[${i}].Replicate`, plot.replicate?.toString() ?? "");
		expDesignForm.set(`FactorPlots[${i}].PlotGroupingDisplayId`, plot.plotGroupingDisplayId ?? "");
		expDesignForm.set(`FactorPlots[${i}].TreatmentDisplayId`, plot.treatmentDisplayId?.toString() ?? "");
		expDesignForm.set(`FactorPlots[${i}].TreatmentName`, plot.treatmentName ?? "");
		expDesignForm.set(`FactorPlots[${i}].Seed`, plot.seed ?? "");
		expDesignForm.set(`FactorPlots[${i}].RecId`, plot.recId ?? "");
	});

	const res = await fetch(`${aerialApiUri}api/v1/Factors/EditExperimentalDesign?clientId=${clientId}`, {
		method: "PUT",
		headers: { Authorization: `Bearer ${accessToken}` },
		body: expDesignForm,
	});

	const data = res.json();
	return data as Promise<AerialPlotResponse<ExperimentalDesignFactor>>;
}

export async function editExperimentalDesignInfo(
	experimentalDesigns: ExperimentalDesignFactor[],
	clientId: string,
	accessToken: string,
): Promise<AerialPlotResponse<ExperimentalDesignFactor[]>> {
	const expDesignInfoForm = new FormData();

	experimentalDesigns.forEach((exp, i) => {
		expDesignInfoForm.set(`ExpDesignsVm[${i}].Id`, exp.id!);
		// @ts-expect-error
		expDesignInfoForm.set(`ExpDesignsVm[${i}].Default`, exp.default);

		exp.factorTreatments.forEach((treatment, ii) => {
			expDesignInfoForm.set(`ExpDesignsVm[${i}].FactorTreatments[${ii}].Id`, treatment.id);
			expDesignInfoForm.set(`ExpDesignsVm[${i}].FactorTreatments[${ii}].Name`, treatment.name);
			if (treatment.treatmentTypeId) {
				expDesignInfoForm.set(`ExpDesignsVm[${i}].FactorTreatments[${ii}].TreatmentTypeId`, treatment.treatmentTypeId);
			}
		});
	});

	const res = await fetch(`${aerialApiUri}api/v1/Factors/EditExperimentalDesignInfo?clientId=${clientId}`, {
		method: "PUT",
		headers: { Authorization: `Bearer ${accessToken}` },
		body: expDesignInfoForm,
	});

	const data = res.json();
	return data as Promise<AerialPlotResponse<ExperimentalDesignFactor[]>>;
}

export async function deleteExperimentalDesigns(
	experimentalDesignIds: string[],
	accessToken: string,
): Promise<AerialPlotResponse<void>> {
	const res = await fetch(`${aerialApiUri}api/v1/Factors/DeleteExperimentalDesigns`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${accessToken}`,
		},
		body: JSON.stringify(experimentalDesignIds),
	});

	const data = res.json();
	return data as Promise<AerialPlotResponse<void>>;
}

export async function convertDataLayerToExperimentalDesign(
	clientId: string,
	factorId: string,
	accessToken: string,
): Promise<AerialPlotResponse<void, Message[]>> {
	const res = await fetch(
		`${aerialApiUri}api/v1/Factors/ConvertDataLayerToExperimentalDesign?clientId=${clientId}&factorId=${factorId}`,
		{
			method: "POST",
			headers: {
				Authorization: `Bearer ${accessToken}`,
			},
		},
	);

	const data = res.json();
	return data as Promise<AerialPlotResponse<void, Message[]>>;
}

export function experimentalDesignsForMFAQueryOptions(
	trialId: string,
	clientId: string,
	getAccessToken: () => Promise<string>,
) {
	return queryOptions<ExperimentalDesignForMFA[], Error>({
		enabled: !!trialId,
		queryKey: ["GetExperimentalDesignFactorsForMFA", trialId, clientId],
		initialData: [],
		queryFn: async () => {
			const accessToken = await getAccessToken();
			const responseModel = await _getExperimentalDesignsForMFA(trialId, clientId, accessToken);

			return responseModel.data;
		},
	});
}

async function _getExperimentalDesignsForMFA(
	trialId: string,
	clientId: string,
	accessToken: string,
): Promise<AerialPlotResponse<ExperimentalDesignForMFA[]>> {
	const res = await fetch(
		`${aerialApiUri}api/v1/Trials/GetExperimentalDesignFactorsForMFA?clientId=${clientId}&trialId=${trialId}`,
		{
			method: "GET",
			headers: { Authorization: `Bearer ${accessToken}` },
		},
	);

	if (!res.ok) {
		throw new Error("failed to get experimental designs for geoMFA");
	}

	const data = res.json();
	return data;
}

export async function generateTreatmentPolygons({
	plotFeatures,
	tolerance,
	clientId,
	getAccessToken,
}: {
	plotFeatures: FeatureCollection<Polygon, { treatment: number }>;
	tolerance: number;
	clientId: string;
	getAccessToken: () => Promise<string>;
}): Promise<AerialPlotResponse<FeatureCollection<Polygon | MultiPolygon, { treatment: number }>>> {
	const accessToken = await getAccessToken();

	const res = await fetch(
		`${apDataPlatformBaseUrl}DataLayers/ExperimentalDesigns/GenerateTreatmentPolygons?clientId=${clientId}&tolerance=${tolerance}`,
		{
			method: "POST",
			headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
			body: JSON.stringify(plotFeatures),
		},
	);

	if (!res.ok) {
		throw new Error("failed to generate polygons for experimental designs");
	}
	const body = await res.json();

	return body as AerialPlotResponse<FeatureCollection<Polygon | MultiPolygon, { treatment: number }>>;
}
